import clsx from 'clsx';
import Icon from 'components/atoms/Icon';
import ListWithIcon from 'components/molecules/ListWithIcon/ListWithIcon';
import { ProjectStatus } from 'generated/graphql';
import { Button, IconType, Modal } from '@spotted-zebra-uk/ui-components';
import styles from './ProjectStatusDropdown.module.scss';

type StatusContent = {
  getTitleLabel: (projectName: string) => string;
  description: string;
  content: React.ReactNode;
  cancelButtonLabel: string;
  confirmButtonLabel: string;
};

// Nested structure: [currentStatus][newStatus] => content
const statusTransitionContent: Record<
  ProjectStatus,
  Partial<Record<ProjectStatus, StatusContent>>
> = {
  [ProjectStatus.Draft]: {
    [ProjectStatus.Published]: {
      getTitleLabel: (projectName) => `Publish ${projectName}`,
      description:
        'Publishing makes the role accessible to clients for assessing candidates. Review carefully and confirm everything is correct before publishing.',
      content: (
        <div className={styles.content}>
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.lockIcon)}>
                <Icon icon="lock" />
              </div>
            }
            title="Locked when published"
            listItems={[
              'Edit Role Skills Profile',
              'Edit assessment norm groups',
              'Add or remove Role Skills Profiles',
            ]}
          />
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.checkIcon)}>
                <Icon icon="check" />
              </div>
            }
            title="Available when published"
            listItems={[
              'Edit role and Role Skills Profile details',
              'Edit hiring team members',
              'Add and remove stages',
              'Edit role and stage settings',
            ]}
          />
        </div>
      ),
      cancelButtonLabel: 'Go back and review',
      confirmButtonLabel: 'Publish role',
    },
    [ProjectStatus.Review]: {
      getTitleLabel: (projectName) => `Transition ${projectName} to in review`,
      description:
        "Transitioning the role to in review enables the role's Role Skills Profile to be edited in Admin.",
      content: (
        <p className={styles.description}>
          Company users will have access to the role but with limited use until
          the Role is published.
        </p>
      ),
      cancelButtonLabel: 'Go back and review',
      confirmButtonLabel: 'Confirm',
    },
  },
  [ProjectStatus.Published]: {
    [ProjectStatus.Draft]: {
      getTitleLabel: (projectName) => `Unpublish ${projectName}`,
      description:
        'Unpublishing by moving the role to draft status will make this role inaccessible to end users. Only unpublish if absolutely necessary.',
      content: (
        <div className={styles.content}>
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.warningIcon)}>
                <Icon icon="warning" />
              </div>
            }
            title="What to be aware of"
            listItems={[
              'Changes to Role Skills Profile may affect Interview Guides',
              'Interview evaluations linked to the role may be lost',
              'Admin-created roles will no longer be accessible to users',
            ]}
          />
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.lightbulbIcon)}>
                <Icon icon="lightbulb" />
              </div>
            }
            title="Alternative options"
            listItems={[
              'Close if hiring cycle has ended',
              'Archive if created by mistake or no longer needed',
            ]}
          />
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.checkIcon)}>
                <Icon icon="check" />
              </div>
            }
            title="Available when in draft"
            listItems={[
              'Edit Role Skills Profile',
              'Edit assessment norm groups',
              'Add or remove Role Skills Profiles',
            ]}
          />
        </div>
      ),
      cancelButtonLabel: 'Cancel',
      confirmButtonLabel: 'Unpublish role',
    },
    [ProjectStatus.Review]: {
      getTitleLabel: (projectName) => `Transition ${projectName} to in review`,
      description:
        'Transitioning the role to in review enables the role’s Role Skills Profile to be edited in Admin.',
      content: (
        <p className={styles.description}>
          Company users will have access to the role but with limited use until
          the Role is published.
        </p>
      ),
      cancelButtonLabel: 'Go back and review',
      confirmButtonLabel: 'Confirm',
    },
    [ProjectStatus.Closed]: {
      getTitleLabel: (projectName) => `Close ${projectName}`,
      description:
        'Users can still access closed roles but cannot add candidates, take assessments, or link to interviews. Closed roles can be republished.',
      content: (
        <div className={styles.content}>
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.lockIcon)}>
                <Icon icon="lock" />
              </div>
            }
            title="Locked when closed"
            listItems={[
              'Add candidates',
              'Invite candidates to assessments or link to interviews',
              'Edit interview or assessment stages',
              'Edit Role Skills Profiles',
            ]}
          />
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.checkIcon)}>
                <Icon icon="check" />
              </div>
            }
            title="Available when closed"
            listItems={[
              'View role and candidate data',
              'Access assessment results and interviews',
            ]}
          />
        </div>
      ),
      cancelButtonLabel: 'Cancel',
      confirmButtonLabel: 'Close role',
    },
  },
  [ProjectStatus.Closed]: {
    [ProjectStatus.Published]: {
      getTitleLabel: (projectName) => `Publish ${projectName}`,
      description:
        'Publishing makes the role accessible to users for candidate assessments.',
      content: (
        <div className={styles.content}>
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.lockIcon)}>
                <Icon icon="lock" />
              </div>
            }
            title="Locked when published"
            listItems={[
              'Edit Role Skills Profile',
              'Edit assessment norm groups',
              'Add or remove roles',
            ]}
          />
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.checkIcon)}>
                <Icon icon="check" />
              </div>
            }
            title="Available when published"
            listItems={[
              'Edit role and Role Skills Profile details',
              'Edit hiring team members',
              'Add and remove stages',
              'Edit role and stage settings',
            ]}
          />
        </div>
      ),
      cancelButtonLabel: 'Go back and review',
      confirmButtonLabel: 'Publish role',
    },
  },
  [ProjectStatus.Review]: {
    [ProjectStatus.Published]: {
      getTitleLabel: (projectName) => `Publish ${projectName}`,
      description:
        'Publishing makes the role accessible to clients for assessing candidates. Review carefully and confirm everything is correct before publishing.',
      content: (
        <div className={styles.content}>
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.lockIcon)}>
                <Icon icon="lock" />
              </div>
            }
            title="Locked when published"
            listItems={[
              'Edit Role Skills Profile',
              'Edit assessment norm groups',
              'Add or remove roles',
            ]}
          />
          <ListWithIcon
            icon={
              <div className={clsx(styles.iconContainer, styles.checkIcon)}>
                <Icon icon="check" />
              </div>
            }
            title="Available when published"
            listItems={[
              'Edit role and Role Skills Profile details',
              'Edit hiring team members',
              'Add and remove stages',
              'Edit role and stage settings',
            ]}
          />
        </div>
      ),
      cancelButtonLabel: 'Go back and review',
      confirmButtonLabel: 'Publish role',
    },
  },
};

const getStatusTransitionContent = (
  currentStatus: ProjectStatus,
  newStatus: ProjectStatus,
): StatusContent => {
  return (
    statusTransitionContent[currentStatus]?.[newStatus] || {
      getTitleLabel: (projectName) => `${newStatus}: ${projectName}`,
      description: '',
      content: null,
      cancelButtonLabel: 'Cancel',
      confirmButtonLabel: 'Confirm',
    }
  );
};

const ProjectStatusChangeModal = ({
  currentStatus,
  newStatus,
  onClose,
  onSubmit,
  loading,
  projectName,
}: {
  projectName: string;
  currentStatus: ProjectStatus;
  newStatus: ProjectStatus;
  onClose: () => void;
  onSubmit: () => void;
  loading: boolean;
}) => {
  const {
    getTitleLabel,
    description,
    content,
    cancelButtonLabel,
    confirmButtonLabel,
  } = getStatusTransitionContent(currentStatus, newStatus);

  return (
    <Modal
      header={getTitleLabel(projectName)}
      headerIconType={IconType.WARNING}
      onClose={onClose}
      isOpen={true}
      className={styles.modalContainer}
    >
      <p className={styles.description}>{description}</p>
      {content}
      <div className={styles.actions}>
        <Button size="medium" variant="secondary" onClick={onClose}>
          {cancelButtonLabel}
        </Button>
        <Button size="medium" onClick={onSubmit} loading={loading}>
          {confirmButtonLabel}
        </Button>
      </div>
    </Modal>
  );
};

export default ProjectStatusChangeModal;
